import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import loginGreenImage from "../assets/login_green.png";
import {
  EyeIcon,
  EyeOffIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/solid";
import { Label } from "../components/ui/label";
import packageJson from "../../package.json"; //importing version number
import { apiversion, profileService, profileDataMap } from "../services";
import { userService, membershipService, membershipDataMap } from "../services";
import Modal from "../components/Modal";
import useUserStore from "../stores/userStore";
import useUserStore2 from "../stores/userStore2";
// import useLoginStore from "../stores/pages/login"

/**
 * @import { App.Store.User, App.Store.Profile, App.Pages.Login } from '../../typedef.js'
 */

import { createPageStore } from "../stores/pages/pageStore";
import { Button } from "../components/ui/button";
import { FaExclamationTriangle } from "react-icons/fa";
const initialState = {
  showPassword: false,
  authenticationString: "",
  password: "",
  emailError: "",
  passwordError: "",
  isEmailValid: false,
  loading: false,
  forgotPasswordModal: false,
  resetEmail: "",
  resetEmailError: "",
  isResetEmailValid: false,
  apiVersion: null,
  showErrorModal: false, // Add this line
  modalError: "", // Add this line to store the modal error message
};
const useLoginStore = createPageStore("login", initialState);

const LoginPage = () => {
  const navigate = useNavigate();
  const { page, setPage } = useLoginStore();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Get the current user from your store
        const { userRole } = useUserStore.getState();
        const { user } = useUserStore2.getState();

        // If either store indicates the user is logged in
        if (userRole || user?.role) {
          const role = userRole || user?.role;

          // Redirect based on role
          switch (role) {
            case "A":
              navigate("/dashboard");
              break;
            case "CA":
              navigate("/comm-dashboard");
              break;
            case "GL":
              navigate("/member-dashboard");
              break;
            case "C":
              navigate("/poccollector");
              break;
            case "POCA":
              navigate("/pocattendant");
              break;
            case "U":
              navigate("/member-dashboard");
              break;
            default:
              navigate("/");
          }
        }
      } catch (error) {
        console.error("Error checking auth status:", error);
        // If there's an error checking auth status, just stay on login page
      }
    };

    checkAuth();
  }, [navigate]);

  // ------------------------------------------------------------------
  // useUserStore to be deprecated
  // ------------------------------------------------------------------
  // Destructure Zustand actions
  const { login, updateProfiles } = useUserStore.getState();
  // ------------------------------------------------------------------

  // ••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  // useUserStore2 to be used going forward
  // ••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
  const {
    setUser,
    updateProfiles: updateProfiles2,
    setCurrentProfile,
    apiDataToUser,
  } = useUserStore2();
  // ••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••

  useEffect(() => {
    const fetchVersion = async () => {
      const version = await apiversion.getVersion();
      setPage((state) => {
        state.apiVersion = version;
      });
    };

    fetchVersion();
  }, []);

  const togglePasswordVisibility = () => {
    setPage((state) => {
      state.showPassword = !state.showPassword;
    });
  };

  const handleEmailBlur = () => {
    // Email validation on blur
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(page.authenticationString)) {
      setPage((state) => {
        state.emailError = "Incorrect Email Format.";
        state.isEmailValid = false;
      });
    } else {
      setPage((state) => {
        state.emailError = "";
        state.isEmailValid = true;
      });
    }
  };

  const handleModalEmailBlur = () => {
    // Email validation on blur
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(page.authenticationString)) {
      setPage((state) => {
        state.resetEmailError = "Incorrect Email Address. Please Try Again.";
        state.isResetEmailValid = false;
      });
    } else {
      setPage((state) => {
        state.resetEmailError = "";
        state.isResetEmailValid = true;
      });
    }
  };

  const validateLoginFields = () => {
    let isValid = true;

    setPage((state) => {
      // Reset errors
      state.emailError = "";
      state.passwordError = "";

      // Check email
      if (!state.authenticationString.trim()) {
        state.emailError = "Email is required.";
        isValid = false;
      } else if (
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
          state.authenticationString
        )
      ) {
        state.emailError = "Please enter a valid email address.";
        isValid = false;
      }

      // Check password
      if (!state.password.trim()) {
        state.passwordError = "Password is required.";
        isValid = false;
      }
    });

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (page.loading) return;

    const isValid = validateLoginFields();
    if (!isValid) return;

    setPage((state) => {
      state.loading = true;
      state.emailError = "";
      state.passwordError = "";
      state.showErrorModal = false;
      state.modalError = "";
    });

    try {
      // console.log("Making login API Call");
      let response = await userService.login(
        page.authenticationString,
        page.password
      );

      // Extract data from response
      const data = response.Data;
      const userId = data._id;
      const firstName = data.first_name;
      const lastName = data.last_name;
      const userRole = data.role;
      const primaryProfileId = data.primary_profile_id?._id;
      const entityId = data._id;
      const primaryProfileName = data.primary_profile_id?.name;
      const profilePicture = data.medias?.[0]?.img_url || null; // Get profile picture

      // Convert API data to user object
      const user = apiDataToUser(data);
      // console.log("Login user data:", user);

      // ••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
      // useUserStore2 implementation
      // ••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••

      // 1. Set logged in user information (includes profilePicture)
      setUser(user);

      // 2. Fetch all memberships for user
      let memberships = [];
      try {
        const membershipInfo = await membershipService.getMembershipById(
          user.id
        );
        memberships = membershipInfo.map(membershipDataMap);
      } catch (membershipError) {
        console.error("Error fetching membership details:", membershipError);
      }

      // 3. Fetch all profiles for user
      const profileListResponse = await profileService.getUserProfiles(userId);

      // Initialize profiles array if it doesn't exist
      if (!Array.isArray(user.profiles)) user.profiles = [];

      // Map profiles and add memberships
      profileListResponse.Data.map(profileDataMap).forEach((profile) => {
        let profileMemberships = [];

        // Add matching memberships to profile
        memberships.forEach((membership) => {
          if (membership.profileId === profile.id) {
            profileMemberships.push(membership);
          }
        });
        profile.memberships = profileMemberships;
        user.profiles.push(profile);
      });

      // console.log("User with profiles:", user);
      updateProfiles2(user.profiles);

      // 4. Set current profile (primary profile)
      const primaryProfile = user.profiles.find(
        (profile) => profile.id === user.primaryProfile.id
      );
      if (primaryProfile) {
        setCurrentProfile(primaryProfile);
      }

      // ••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
      // Legacy useUserStore implementation (to be deprecated)
      // ••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
      let profileRole = null;
      let communityId = null;
      let groupId = null;

      // Fetch membership details if primaryProfileId exists
      if (primaryProfileId) {
        try {
          const membershipInfo = await membershipService.getMembershipById(
            entityId,
            primaryProfileId
          );
          const memberships = membershipInfo.map((membership) => ({
            membershipId: membership?._id || null,
            role: membership?.role || null,
            status: membership?.status || null,
            profileId: membership?.profile?._id || null,
            profileName: membership?.profile?.name || null,
            communityId: membership?.community?._id || null,
            communityName: membership?.community?.name || null,
            groupId: membership?.group?._id || null,
            groupName: membership?.group?.name || null,
            entityId: membership?.entity?._id || null,
            entityName: membership?.entity?.name || null,
            createDate: membership?.create_date || null,
            lastUpdate: membership?.last_update || null,
          }));

          if (memberships.length > 0) {
            const primaryMembership = memberships[0];
            profileRole = primaryMembership.role || null;
            communityId = primaryMembership.communityId || null;
            groupId = primaryMembership.groupId || null;
          }
        } catch (membershipError) {
          console.error("Error fetching membership details:", membershipError);
        }
      }

      // Update legacy store
      login({
        userId,
        firstName,
        lastName,
        userRole,
        primaryProfileId,
        primaryProfileName,
        profileRole: profileRole ?? useUserStore.getState().profileRole,
        communityId: communityId ?? useUserStore.getState().communityId,
        groupId: groupId ?? useUserStore.getState().groupId,
        currentProfile:
          primaryProfileName ?? useUserStore.getState().currentProfile,
        profilePicture, // Add profile picture to legacy store
      });

      // Fetch and store profile list in legacy Zustand store
      if (userId) {
        try {
          const profileListResponse = await profileService.getUserProfiles(
            userId
          );
          if (profileListResponse && !profileListResponse.Error) {
            const profileList = profileListResponse.Data.map((profile) => ({
              id: profile._id,
              name: profile.name,
              dateCreated: profile.create_date,
            }));
            updateProfiles(profileList);
          }
        } catch (profileError) {
          console.error("Error fetching profile list:", profileError);
        }
      }

      // Role-based redirection
      switch (userRole) {
        case "A":
          navigate("/dashboard");
          break;
        case "CA":
          navigate("/comm-dashboard");
          break;
        case "GL":
          navigate("/member-dashboard");
          break;
        case "C":
          navigate("/poccollector");
          break;
        case "POCA":
          navigate("/pocattendant");
          break;
        case "U":
          navigate("/member-dashboard");
          break;
        default:
          navigate("/");
      }
    } catch (error) {
      const errorDesc =
        error?.response?.data?.ErrorDesc ||
        "An unexpected error occurred. Please try again later.";

      if (errorDesc === "Authentication failed") {
        setPage((state) => {
          state.passwordError = errorDesc;
        });
      } else {
        setPage((state) => {
          state.showErrorModal = true;
          state.modalError = errorDesc;
        });
      }
    } finally {
      setPage((state) => {
        state.loading = false;
        state.password = "";
      });
    }
  };

  const handleForgotPassword = () => {
    setPage((state) => {
      state.forgotPasswordModal = true;
    });
  };

  const handleCloseModal = () => {
    setPage((state) => {
      state.forgotPasswordModal = false;
      state.resetEmail = "";
      state.resetEmailError = "";
    });
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();

    // Check if email is empty
    if (!page.resetEmail) {
      setPage((state) => {
        state.resetEmailError = "Email is required.";
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(page.resetEmail)) {
      setPage((state) => {
        state.resetEmailError =
          "Invalid email format. Please enter a valid email.";
      });
      return;
    }

    // Clear previous errors
    setPage((state) => {
      state.resetEmailError = "";
    });

    try {
      console.log("Submitting forgot password request for:", page.resetEmail);

      // Call the API function
      await userService.forgotPasswordRequest(page.resetEmail);

      alert("Password reset instructions have been sent to your email.");
      handleCloseModal(); // ✅ Close modal after success
    } catch (error) {
      console.error("Error sending reset password request:", error);
      setPage((state) => {
        state.resetEmailError =
          "Failed to send reset request. Please try again.";
      });
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4 md:px-0">
      {/* Responsive Box */}
      <div className="flex flex-col md:flex-row w-full max-w-4xl h-auto md:h-[480px] bg-white shadow-lg rounded-3xl overflow-hidden">
        {/* Left Section with Image */}
        <div className="md:w-3/5 w-full bg-green-500 relative hidden md:block">
          <img
            src={loginGreenImage}
            alt="Login Visual"
            className="object-cover w-full h-full"
          />

          <div className="absolute bottom-12 right-12 text-white font-bold text-3xl space-y-1 text-right">
            <div>Hey</div>
            <div>Welcome</div>
            <div>Back</div>
          </div>
        </div>

        {/* Right Section with Form */}
        <div className="md:w-2/5 w-full p-10 flex flex-col justify-center">
          {/* Center-Aligned Login Header */}
          <h2 className="text-xl font-bold text-gray-700 text-center mb-8 pt-4">
            Login
          </h2>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            {/* Email Input */}
            <div className="mb-6 relative">
              <Label htmlFor="page.authenticationString">Email Address</Label>

              <div className="relative">
                <input
                  type="email"
                  id="page.authenticationString"
                  value={page.authenticationString}
                  onChange={(e) =>
                    setPage((state) => {
                      state.authenticationString = e.target.value;
                    })
                  }
                  onBlur={handleEmailBlur}
                  className={`w-full pr-10 px-4 py-2 border-b-2 focus:outline-none focus:ring-0 ${
                    page.emailError
                      ? "border-red-500 focus:border-red-500"
                      : "border-gray-300 focus:border-green-500"
                  } ${!page.emailError ? "hover:border-green-500" : ""}`}
                  placeholder="Enter your email"
                  required
                />

                {/* Validation Icons */}
                {page.emailError && (
                  <ExclamationCircleIcon className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 h-5 w-5" />
                )}
                {page.isEmailValid &&
                  !page.emailError &&
                  page.authenticationString && (
                    <CheckCircleIcon className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500 h-5 w-5" />
                  )}
              </div>

              {/* Error Message */}
              {page.emailError && (
                <p className="text-sm text-red-500 mt-2">{page.emailError}</p>
              )}
            </div>

            {/* Password Input with Eye Icon */}
            <div className="mb-6 relative">
              <Label htmlFor="password">Password</Label>
              <input
                type={page.showPassword ? "text" : "password"} // Corrected input type
                id="password"
                value={page.password}
                onChange={(e) =>
                  setPage((state) => {
                    state.password = e.target.value;
                  })
                }
                className={`w-full px-4 py-2 border-b-2 focus:outline-none focus:ring-0 ${
                  page.passwordError
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-300 focus:border-green-500"
                } ${!page.passwordError ? "hover:border-green-500" : ""}`}
                placeholder="Enter your password"
                required
              />
              {page.passwordError ? null : (
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center justify-center text-gray-500 mt-3 top-1/2 transform -translate-y-1/2"
                  onClick={togglePasswordVisibility}
                >
                  {page.showPassword ? (
                    <EyeIcon className="h-5 w-5" />
                  ) : (
                    <EyeOffIcon className="h-5 w-5" />
                  )}
                </button>
              )}
              {page.passwordError && (
                <>
                  <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500 h-5 w-5" />
                  <p className="text-sm text-red-500 mt-2">
                    {page.passwordError}
                  </p>
                </>
              )}
            </div>

            {/* Remember Me & Padding Between Login Button */}
            {/* <div className="flex items-center justify-between mb-8">
              <Label>
                <input
                  type="checkbox"
                  className="form-checkbox text-green-500"
                />
                <span className="ml-2">Remember Me</span>
              </Label>
            </div> */}

            {/* Login Button */}
            <button
              className="w-full px-4 py-2 text-white bg-blue-400 rounded-md focus:outline-none focus:ring-2 mb-6 flex justify-center items-center"
              type="submit"
              disabled={page.loading}
            >
              {page.loading ? (
                <svg
                  className="animate-spin h-5 w-5 mr-2 border-2 border-white border-t-transparent rounded-full"
                  viewBox="0 0 24 24"
                />
              ) : (
                "Login"
              )}
            </button>
          </form>

          {/* Forgot Password */}
          <button
            className="block text-center text-sm text-gray-500 hover:underline"
            onClick={handleForgotPassword}
          >
            Forgot Password?
          </button>

          <div className="text-center text-xs text-gray-500 mt-4">
            App version {packageJson.version}
            <br />
            API version {page.apiVersion}
          </div>
        </div>
      </div>

      {/* Error Modal */}
      <Modal
        isOpen={page.showErrorModal}
        onClose={() =>
          setPage((state) => {
            state.showErrorModal = false;
            state.modalError = "";
          })
        }
        title="Login Unsuccessful"
        description={page.modalError}
        icon={<FaExclamationTriangle />}
        iconColor="red"
        actions={
          <div className="flex justify-center w-full">
            <Button
              onClick={() =>
                setPage((state) => {
                  state.showErrorModal = false;
                  state.modalError = "";
                })
              }
            >
              Ok
            </Button>
          </div>
        }
      />

      {/* Forgot Password Modal */}
      {page.forgotPasswordModal && (
        <Modal isOpen={page.forgotPasswordModal} onClose={handleCloseModal}>
          <div className="p-6">
            {/* Lock Icon */}
            <div className="flex justify-center mb-4">
              <LockClosedIcon className="h-12 w-12 text-gray-600" />
            </div>

            {/* Heading */}
            <h2 className="text-xl font-semibold text-center text-gray-800">
              Reset Password
            </h2>

            {/* Body Text */}
            <p className="text-sm text-center text-gray-600 mt-2">
              Enter your email address to get instructions to reset your
              password.
            </p>

            {/* Email Input */}
            <div className="mt-4">
              <Label htmlFor="resetEmail">Email Address</Label>
              <input
                type="email"
                id="resetEmail"
                value={page.resetEmail}
                onChange={(e) =>
                  setPage((state) => {
                    state.resetEmail = e.target.value;
                  })
                }
                onBlur={handleModalEmailBlur} // ✅ Validate on blur
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2
            ${
              page.resetEmailError
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-green-500"
            }`}
                placeholder="Enter your email"
                required
              />
              {page.resetEmailError && (
                <p className="text-sm text-red-500 mt-2">
                  {page.resetEmailError}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex justify-between mt-6">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPasswordSubmit}
                className="px-4 py-2 text-white bg-green-500 rounded-md hover:bg-green-600"
              >
                Submit
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default LoginPage;
